import type { UseQueryResult } from '@tanstack/react-query'
import { useFormikContext } from 'formik'
import { useEffectOnce } from 'react-use'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  QueryLoadingManager,
  useAccessTokenRoles,
} from '@b-stock/fe-core/bstock-next/components'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  getSelectedQuoteQuery,
  locationEligibilityQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { SpinnerDots } from '@b-stock/fe-core/bstock-react'
import { LocationAttribute } from '@b-stock/location-api-client'
import type { ActiveServiceDto } from '@b-stock/subscription-api-client'

import {
  type BidFormFormikValues,
  BidFormProvider,
  useBidFormContext,
} from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { useAuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import {
  getAutoPayTarget,
  getHasAutoPay,
} from '@/buyer-portal/components/features/listing/actions/auction/utils'
import useAuctionDetailsUpdates from '@/buyer-portal/components/features/listing/hooks/useAuctionDetailsUpdates'
import { useAutoPaymentMethods } from '@/buyer-portal/components/features/listing/hooks/useAutoPaymentMethods'
import { useActivePhase } from '@/buyer-portal/helpers/auctions/buyingFormat'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { getUserMinBidAmountForAuction } from '@/helpers/getUserMinBidAmount'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import searchListingDetailsQuery from '@/queries/searchListingDetailsQuery'

import { FormError } from '../ActionForm/FormError'
import { CenteredFormRow } from '../ActionForm/ui'
import { AuctionActionCellContent } from './AuctionActionCellContent'
import { getBidFormId } from './getBidFormId'
import { QuoteGate } from './QuoteGate'

const ns = 'Listings.ListView.actionCell.auction'

/**
 * Centers the spinner within the same span the bid form occupies, rather
 * than letting the panel's `justify-content: space-between` collapse it to
 * the row's flex-start.
 */
const LoadingSpinner = () => (
  <CenteredFormRow>
    <SpinnerDots />
  </CenteredFormRow>
)

export const useAuctionActionPostloadQuery = ({
  searchListing,
  // Defaults on: listingQuery already treated undefined as enabled, so an
  // omitted flag silently disabled only the prefetches below.
  enabled = true,
}: {
  searchListing: CombinedListingSearchResult
  enabled?: boolean
}) => {
  const { listingId, sellerId } = searchListing

  /**
   * We expect an accountId since guest users see "Sign in to Bid".
   */
  const accountId = useAccountId()
  const roles = useAccessTokenRoles()
  const isActiveBuyer = roles?.buyer === RoleStatus.Active
  const isServiceAccountOnly = useIsServiceAccountOnly()

  const userMayNeedQuote = !!accountId && !isServiceAccountOnly && isActiveBuyer

  const listingQuery = searchListingDetailsQuery.useWithOpts(
    [searchListing, accountId],
    {
      enabled: !!accountId && enabled,
    }
  )

  /**
   * Prefetch the setup queries that useAutoQuoteGeneration needs. This ensures
   * that these queries will be cache hits inside useAutoQuoteGeneration.
   */
  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        accountId: [accountId ?? ''],
      },
    ],
    { enabled: userMayNeedQuote && enabled }
  )
  const defaultLocation = myLocations.data?.locations[0]

  const selectedQuoteQuery = getSelectedQuoteQuery.useWithOpts(
    [listingId, accountId ?? undefined],
    {
      enabled:
        userMayNeedQuote &&
        enabled &&
        (!!defaultLocation?.id || myLocations.isFetched),
    }
  )

  const subscriptionQuery = activeSubscriptionsQuery.useWithOpts([sellerId], {
    enabled,
  })

  locationEligibilityQuery.useWithOpts(
    [{ listingId, locationId: [defaultLocation?.id ?? ''] }],
    {
      enabled: userMayNeedQuote && enabled && !!defaultLocation,
    }
  )

  return { listingQuery, selectedQuoteQuery, subscriptionQuery }
}

/**
 * This component is lazy-loaded: we wait until user submits preload form.
 */
export const AuctionActionCellPostload = ({
  bidType,
  headless = false,
  initialBidAmount,
  searchListing,
}: {
  bidType?: BidFormFormikValues['bidType']
  headless?: boolean
  initialBidAmount?: number
  searchListing: CombinedListingSearchResult
}) => {
  const { listingQuery, selectedQuoteQuery, subscriptionQuery } =
    useAuctionActionPostloadQuery({
      searchListing,
    })

  return (
    <QueryLoadingManager
      {...listingQuery}
      EmptyComponent={EmptyComponent}
      ErrorComponent={ErrorComponent}
      LoadingComponent={LoadingSpinner}
    >
      {({ data }) => (
        <ListingDataConsumer
          listing={data as OrchestratedAuctionListingDetails}
          bidType={bidType}
          headless={headless}
          initialBidAmount={initialBidAmount}
          searchListing={searchListing}
          selectedQuoteQuery={selectedQuoteQuery}
          /**
           * We cannot load subscription with listing in a MultiQueryLoadingManager
           * since subscription may be null and 404 must not be treated as an error.
           */
          subscriptionQuery={subscriptionQuery}
        />
      )}
    </QueryLoadingManager>
  )
}

const ListingDataConsumer = ({
  bidType,
  headless,
  initialBidAmount,
  listing,
  searchListing,
  selectedQuoteQuery,
  subscriptionQuery,
}: {
  bidType?: BidFormFormikValues['bidType']
  headless: boolean
  initialBidAmount?: number
  listing: OrchestratedAuctionListingDetails
  searchListing: CombinedListingSearchResult
  selectedQuoteQuery: UseQueryResult<OrchestratedQuote | null>
  subscriptionQuery: UseQueryResult<ActiveServiceDto | null>
}) => {
  const { autoPay } = listing
  const effectiveInitialBidAmount = headless
    ? getUserMinBidAmountForAuction(listing.auction)
    : initialBidAmount
  const { data: selectedQuote } = selectedQuoteQuery
  const { data: sellerSubscription, isLoading: isLoadingSubscription } =
    subscriptionQuery
  const accountId = useAccountId()
  const auctionDetailState = useAuctionDetailState(listing)
  // GLOB-4832: tag this bid's tracking with whether Freeze Sort is active on
  // the originating List View — see useTrackBidConfirm.
  const { state: listingSearchState, recordBidPlacedWhileFrozen } =
    useListingSearch()

  /**
   * Enable Pusher updates for this listing. Updates both listingDetailsQuery
   * and searchListingDetailsQuery caches so inline bid modal data (e.g.
   * actualEndTime after popcorn bidding) stays current.
   * [PERF] Consider sharing a pusher client across loaded inline bid forms.
   */
  useAuctionDetailsUpdates(listing)

  /**
   * Simultaneously prefetch payment methods, since we will immediately open
   * BidConfirmationModal which uses them. payForId and payForType are included
   * in query key. Payable amount, used to determine default, is not needed.
   */
  const autoPayTarget = getAutoPayTarget(listing)
  const { isLoading: isLoadingPaymentMethods } = useAutoPaymentMethods({
    accountId,
    enabled: !!autoPayTarget && getHasAutoPay(listing, selectedQuote),
    payForId: autoPayTarget?.payForId,
    payForType: autoPayTarget?.payForType,
  })

  if (isLoadingSubscription || (autoPay && isLoadingPaymentMethods)) {
    return <LoadingSpinner />
  }

  return (
    <BidFormProvider
      auctionDetailState={auctionDetailState}
      bidLocation="list"
      // 002-multi-currency Phase 4 / US2 (T263/T264): thread the listing's
      // currency through to the bid form so a buyer bidding on a CAD/GBP/EUR
      // listing sees the correct glyph + amount formatting throughout. The
      // installed @b-stock/listing-api-client predates the multi-currency
      // rollout so `currency` isn't typed on ListingDto yet; cast through
      // (same transitional pattern as T201/T202, T251/T252, T253/T254,
      // T261/T262) with a USD fallback for legacy pre-multi-currency
      // listings (SC-007).
      currency={(listing as { currency?: string }).currency ?? 'USD'}
      doNotify={headless}
      formId={getBidFormId(searchListing.listingId)}
      initialBidAmount={effectiveInitialBidAmount}
      initialBidType={bidType}
      isSortFrozen={listingSearchState.isSortFrozen}
      listing={listing}
      recordBidPlacedWhileFrozen={recordBidPlacedWhileFrozen}
      sellerSubscription={sellerSubscription}
    >
      {headless ? (
        <BidFormConsumer headless searchListing={searchListing} />
      ) : (
        <QuoteGate listing={listing}>
          <BidFormConsumer headless={false} searchListing={searchListing} />
        </QuoteGate>
      )}
    </BidFormProvider>
  )
}

const BidFormConsumer = ({
  headless,
  searchListing,
}: {
  headless: boolean
  searchListing: CombinedListingSearchResult
}) => {
  const formik = useFormikContext<BidFormFormikValues>()
  const {
    buyNowActions: { quickBuy },
  } = useBidFormContext()
  const activePhase = useActivePhase(searchListing.originalSearchResult)

  /**
   * Auto-submit to complete the initial CTA click action on load.
   */
  useEffectOnce(() => {
    // "Buy Now" action: launch modal directly. Does not use form.
    if (activePhase === 'BINO') {
      quickBuy()
      return
    }
    // "Bid Now" action: submit bid form. (Validate, launch modal.)
    void formik.submitForm()
  })

  if (headless) return null

  return (
    <AuctionActionCellContent
      activePhase={activePhase}
      onClickBuyNow={quickBuy}
      searchListing={searchListing}
    />
  )
}

const EmptyComponent = () => <FormError messageId={`${ns}.error.empty`} />

const ErrorComponent = () => <FormError messageId={`${ns}.error.unexpected`} />
